import { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Card } from '../../components/Card';
import { CardHeading } from '../components/CardHeading';
import {
    Bar,
    BarChart,
    Cell,
    ResponsiveContainer,
    XAxis
} from 'recharts';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CheckinState, ISO8601Month } from '../model/CheckinState';
import { IFeeling } from '../model/ICheckin';
import { FEELING_COLORS, FeelingIcon } from '../components/FeelingIcon';
import { Text } from '../../components/Text';
import { Href } from '../../components/Href';

const FeelingIconTick: FC<any> = ({ x, y, payload }) => {
    // TODO: how do we return the IconComponent (which is rendered as an SVG) or FeelingIcon, or...?
    //  so we're not copy / pasted the actual svg itself

    const iconSize = 32;
    return (
        <FeelingIcon
            isSVG
            feeling={payload.value as IFeeling}
            svgLayout={{x: x - (iconSize / 2), y: y + 4, width: iconSize, height: iconSize}}
        />
    );
};

type IFeelingChartCardProps = {
    month: ISO8601Month, // YYYY-MM
    scrollerRef?: React.RefObject<HTMLIonContentElement>
};

export const FeelingChartCard: FC<IFeelingChartCardProps> = ({ month, scrollerRef }) => {
    const checkinState = useInstance(CheckinState);

    const [data, setData] = useState<{feeling: IFeeling, cnt: number}[]>();
    const [hasCheckins, setHasCheckins] = useState<boolean>(false);

    useEffect(() => {
        const selectedMonth = dayjs(month);
        const personalCheckins = checkinState.getPersonalCheckins();
        const checkinsForMonth = personalCheckins.filter(checkin => selectedMonth.isSame(checkin.timestamp, 'month'));
        const chartData = [{
            feeling: 5, cnt: 0
        }, {
            feeling: 4, cnt: 0
        }, {
            feeling: 3, cnt: 0
        }, {
            feeling: 2, cnt: 0
        }, {
            feeling: 1, cnt: 0
        }];

        let hasCheckinData = false;
        for(const checkin of checkinsForMonth) {
            const chkIn = chartData.find(d => d.feeling === checkin.feeling);
            if (chkIn) {
                chkIn.cnt++;
                hasCheckinData = true;
            }
        }

        setData(chartData);
        setHasCheckins(hasCheckinData);
    }, [month, checkinState.state.personalNotes]); // eslint-disable-line react-hooks/exhaustive-deps


    return (
        <Card dataProps={{dataId: 'feeling-chart-card'}} padded>
            <CardHeading>Daily Feeling</CardHeading>
            {!hasCheckins && (
                <Text type="subtitle">
                    Add <Href onClick={() => scrollerRef?.current?.scrollToTop(500)}>journal entries</Href> for tracking this month!
                </Text>
            )}
            <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data} margin={{top: 15, bottom: 15}}>
                    <XAxis
                        axisLine={false}
                        tickLine={false}
                        dataKey="feeling"
                        tick={<FeelingIconTick />}
                    />
                    <Bar
                        dataKey="cnt"
                        radius={[8, 8, 0, 0]}
                        label={{ position: 'top', fill: 'var(--ion-text-color)', fontWeight: 800, style: {opacity: 1} }}
                    >
                        {data?.map((point, idx) => (
                            <Cell
                                key={`cell-${idx}`}
                                style={{opacity: 0.5}}
                                fill={`var(--ion-color-${FEELING_COLORS[point.feeling]}`}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Card>
    );
};
